import { useCallback, useEffect, useState } from "react";
import {
  UI_FEATURES,
  UI_FEATURE_GROUPS,
  SITE_MODES,
  sanitizeUiSettings,
  inClassNow,
  kidsNow,
  uiVisible,
  type SiteMode,
  type UiFeatureDoc,
} from "@greg-chick/shared";
import { adminApi, adminToken } from "../lib/contentApi";
import { Msg, PageHead } from "./ui";
import { useRegisterSave } from "../lib/saveBar";

/**
 * 🎚️ **화면 보이기/숨기기** — 어디에 쓰느냐에 맞춰 켜고 끈다(FR-ADMIN-21).
 *
 * ## 왜 필요한가
 * 같은 서비스를 서로 다른 자리에 내놓는다. **학교 수업**에서는 굿즈·커뮤니티가 보이면 안 되고,
 * **행사장 부스**에서는 그날 쓰는 것만 남겨야 아이가 흩어지지 않는다.
 * 예전엔 코드에 박혀 있어서 운영자가 못 고쳤다 — 학교마다, 행사마다 사정이 다른데.
 *
 * ## 🔴 「지금 어떻게 보이는가」를 같이 보여 준다
 * 스위치만 늘어놓으면 **수업 중 기본값**과 섞여 헷갈린다. 굿즈는 안 껐는데도 수업 중엔
 * 안 보이기 때문이다. 그래서 칸마다 「집에서 / 수업 중」 지금 상태를 함께 적는다.
 *
 * ## 안 고른 것은 안 저장한다
 * 저장하는 것은 **운영자가 직접 만진 것만**이다. 그래야 새 기능이 생겨도
 * 이 설정을 안 건드리고, 아이 화면에서 조용히 사라지지 않는다.
 */
export function UiFeaturesPanel() {
  const [doc, setDoc] = useState<UiFeatureDoc>({});
  const [mode, setMode] = useState<SiteMode>("auto");
  const [msg, setMsg] = useState<string | null>(null);
  const [kind, setKind] = useState<"ok" | "warn">("ok");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let alive = true;
    void fetch(`${adminApi()}/admin/ui-features`, {
      headers: { "x-admin-token": adminToken() },
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((b: unknown) => {
        if (!alive) return;
        const s = sanitizeUiSettings(b);
        setDoc(s.features);
        setMode(s.mode);
      })
      .catch(() => {
        if (!alive) return;
        setKind("warn");
        setMsg("지금 설정을 못 불러왔어요. 저장하면 지금 화면대로 덮어써요.");
      });
    return () => {
      alive = false;
    };
  }, []);

  const save = useCallback(async () => {
    const res = await fetch(`${adminApi()}/admin/ui-features`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-admin-token": adminToken() },
      body: JSON.stringify({ mode, features: doc }),
    });
    if (!res.ok) throw new Error(`저장 실패 (${res.status})`);
    setDirty(false);
    setKind("ok");
    setMsg("저장했어요. 열려 있는 아이 화면도 바로 바뀝니다.");
  }, [doc, mode]);

  // 저장은 하단 고정 바 하나로 — 패널 안에 저장 버튼을 두지 않는다(관리자 UI 규칙)
  useRegisterSave({
    id: "uifeatures",
    label: "화면 보이기/숨기기",
    dirty: dirty ? 1 : 0,
    save,
    draft: {
      data: () => ({ mode, features: doc }),
      restore: (d) => {
        const s = sanitizeUiSettings(d);
        setDoc(s.features);
        setMode(s.mode);
      },
    },
  });

  function toggle(key: string, on: boolean) {
    setDoc((d) => ({ ...d, [key]: on }));
    setDirty(true);
  }
  function reset(key: string) {
    setDoc((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });
    setDirty(true);
  }

  return (
    <section className="panel">
      <PageHead
        icon="🎚️"
        title="화면 보이기/숨기기"
        desc="학교 수업·행사장처럼 쓰는 자리에 맞춰 아이 화면에서 켜고 꺼요"
      />
      <Msg text={msg} kind={kind} />

      {/* ── 🏫 이 사이트를 지금 무엇으로 쓰나 ─────────────────── */}
      <div className="feat__group feat__group--mode">
        <h2 className="feat__title">
          이 사이트를 지금 무엇으로 쓰나요{" "}
          <span className="feat__hint">아래 설정보다 먼저 정해요</span>
        </h2>
        <ul className="mode__list" role="radiogroup" aria-label="사이트 모드">
          {SITE_MODES.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                role="radio"
                aria-checked={mode === m.id}
                className="mode__card"
                data-on={mode === m.id ? "1" : "0"}
                onClick={() => {
                  setMode(m.id);
                  setDirty(true);
                }}
              >
                <b className="mode__label">{m.label}</b>
                <span className="mode__desc">{m.desc}</span>
                <span className="mode__when">{m.when}</span>
              </button>
            </li>
          ))}
        </ul>
        {mode !== "auto" ? (
          <p className="mode__note">
            {mode === "class"
              ? "🏫 지금은 누가 들어와도 수업 화면이에요. 아래 목록의 「수업 중」이 곧 아이가 보는 화면이에요."
              : "🏠 지금은 수업 링크(?class=1)로 들어와도 일반 화면이에요. 방과후 수업을 다시 시작하면 「자동」이나 「방과후 수업 모드」로 바꿔 주세요."}
          </p>
        ) : null}
      </div>

      <p className="feat__lead">
        <b>안 만지면 그대로 보여요.</b> 만진 것만 저장돼서, 새 기능이 생겨도 여기 설정 때문에
        사라지지 않아요.
      </p>

      {UI_FEATURE_GROUPS.map((g) => {
        const rows = UI_FEATURES.filter((f) => f.group === g.id);
        if (rows.length === 0) return null;
        return (
          <div key={g.id} className="feat__group">
            <h2 className="feat__title">
              {g.label} <span className="feat__hint">{g.hint}</span>
            </h2>
            <ul className="feat__list">
              {rows.map((f) => {
                const picked = doc[f.key];
                // 🔴 미리보기도 **아이 화면과 같은 판정 함수**를 쓴다(관리자에선 되는데
                //    화면은 안 바뀐다를 막는 유일한 방법 — previewParity)
                /**
                 * 🔴 **영유아 모드 축도 넘긴다** (2026-09-23).
                 *
                 * 예전엔 kids 를 안 넘겨서, 운영자가 「🧸 영유아 모드」를 골라도 이 목록이
                 * **하나도 안 바뀌었다** — 「집에서 보임」이라고 적힌 것이 아이 화면에서는
                 * 안 보인다. 「관리자에선 되는데 화면은 안 바뀐다」의 전형이다.
                 */
                const kids = kidsNow(mode);
                const atHome = uiVisible(f.key, doc, inClassNow(mode, false), kids);
                const inClass = uiVisible(f.key, doc, inClassNow(mode, true), kids);
                return (
                  <li
                    key={f.key}
                    className="feat__row"
                    data-picked={picked === undefined ? "0" : "1"}
                  >
                    <div className="feat__what">
                      <b>{f.label}</b>
                      <span className="feat__desc">{f.desc}</span>
                    </div>
                    <span className="feat__state">
                      {mode === "class"
                        ? `지금 ${inClass ? "보임" : "숨김"}`
                        : mode === "home" || mode === "kids"
                          ? `지금 ${atHome ? "보임" : "숨김"}`
                          : `집에서 ${atHome ? "보임" : "숨김"} · 수업 중 ${inClass ? "보임" : "숨김"}`}
                    </span>
                    <div className="feat__pick" role="group" aria-label={`${f.label} 보이기 설정`}>
                      <button
                        type="button"
                        className="btn btn--sm"
                        aria-pressed={picked === true}
                        onClick={() => toggle(f.key, true)}
                      >
                        보이기
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm"
                        aria-pressed={picked === false}
                        onClick={() => toggle(f.key, false)}
                      >
                        숨기기
                      </button>
                      {/* 🔴 「안 고름」으로 되돌리는 길이 있어야 한다 — 한 번 만지면
                          수업 중 기본값이 영영 안 먹는 상태가 되기 때문이다 */}
                      <button
                        type="button"
                        className="btn btn--sm"
                        aria-pressed={picked === undefined}
                        title="기본값으로 — 수업 중에는 알아서 숨겨져요"
                        onClick={() => reset(f.key)}
                      >
                        기본
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </section>
  );
}
